import React, { useState, useEffect } from 'react';
import Layout from 'src/layout';
import axiosInstance from '../../lib/axiosInstance';
import Container from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  RiUserLine,
  RiShoppingCart2Line,
  RiMoneyDollarCircleLine,
  RiBox3Line,
  RiTruckLine,
  RiLineChartLine,
  RiFolderAddLine,
  RiCouponLine,
  RiCheckDoubleLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiRefreshLine,
  RiArrowRightUpLine,
  RiShieldCheckLine
} from 'react-icons/ri';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [shippingFee, setShippingFee] = useState('0');
  const [updatingFee, setUpdatingFee] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/admin/settings');
      if (response.data.success) {
        setShippingFee(response.data.data.shipping_fee || '0');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleUpdateShippingFee = async () => {
    try {
      setUpdatingFee(true);
      const response = await axiosInstance.post('/admin/settings/update', {
        key: 'shipping_fee',
        value: shippingFee
      });
      if (response.data.success) {
        toast.success('Shipping fee updated successfully!');
      } else {
        toast.error('Failed to update shipping fee');
      }
    } catch (error) {
      console.error('Error updating shipping fee:', error);
      toast.error('Failed to update shipping fee');
    } finally {
      setUpdatingFee(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      const response = await axiosInstance.get('/admin/dashboard/stats');
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error.response?.data || error.message);
      setDashboardData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'delivered' || s === 'successful') {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (s === 'pending' || s === 'preparing') {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (s === 'cancelled' || s === 'failed' || s === 'returned') {
      return 'bg-rose-50 text-rose-700 border-rose-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Layout title={'Dashboard'} active={'dashboard-m'}>
      <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
        <Container>
          {/* Executive Header & Quick Actions */}
          <div className="mb-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                Executive Store Overview
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Real-time performance metrics, operational overview, and store settings.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <Button
                onClick={fetchDashboardData}
                disabled={refreshing}
                variant="outline"
                className="bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-sm text-xs h-9"
              >
                <RiRefreshLine className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh Data
              </Button>
              <Button
                onClick={() => navigate('/analytics')}
                className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs h-9 shadow-sm"
              >
                <RiLineChartLine className="w-3.5 h-3.5 mr-1.5" /> Analytics Engine
              </Button>
              <Button
                onClick={() => navigate('/products/add')}
                variant="outline"
                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 text-xs h-9"
              >
                <RiFolderAddLine className="w-3.5 h-3.5 mr-1" /> Add Product
              </Button>
              <Button
                onClick={() => navigate('/coupons/list')}
                variant="outline"
                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 text-xs h-9"
              >
                <RiCouponLine className="w-3.5 h-3.5 mr-1" /> Coupons
              </Button>
            </div>
          </div>

          {/* Core Metric Cards */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm animate-pulse h-28" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Revenue */}
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Revenue</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {formatPrice(dashboardData?.stats?.totalRevenue)}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-xl">
                    <RiMoneyDollarCircleLine className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                  <RiArrowRightUpLine className="w-4 h-4" /> Live Store Gross Sales
                </div>
              </div>

              {/* Total Orders */}
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Orders</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {dashboardData?.stats?.totalOrders || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded-xl">
                    <RiShoppingCart2Line className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-indigo-700 font-medium">
                  <RiCheckDoubleLine className="w-4 h-4" /> Placed Customer Orders
                </div>
              </div>

              {/* Total Users */}
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Registered Users</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {dashboardData?.stats?.totalUsers || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <RiUserLine className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-blue-700 font-medium">
                  <RiShieldCheckLine className="w-4 h-4" /> Customer User Accounts
                </div>
              </div>

              {/* Total Products */}
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Catalog</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {dashboardData?.stats?.totalProducts || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-xl">
                    <RiBox3Line className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 font-medium">
                  <RiBox3Line className="w-4 h-4" /> Products in Inventory
                </div>
              </div>
            </div>
          )}

          {/* Shipping Fee Controls Card */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-50 rounded-xl">
                  <RiTruckLine className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Standard Shipping Fee Settings</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Configure standard shipping charge automatically applied on orders below ₹999 threshold.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-36">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    value={shippingFee}
                    onChange={(e) => setShippingFee(e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    placeholder="0"
                  />
                </div>
                <Button
                  onClick={handleUpdateShippingFee}
                  disabled={updatingFee}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-medium text-xs h-10 px-5 shadow-sm"
                >
                  {updatingFee ? 'Updating...' : 'Save Shipping Fee'}
                </Button>
              </div>
            </div>
          </div>

          {/* Breakdown Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Order Status Breakdown */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-semibold text-slate-900 pb-2 border-b border-slate-100">
                Order Status Breakdown
              </h3>
              {loading ? (
                <div className="space-y-3 py-4">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="h-4 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3.5">
                  {dashboardData?.breakdown?.orderStatus?.map((item, idx) => {
                    const total = dashboardData.stats.totalOrders || 1;
                    const percent = Math.round(((item.count || 0) / total) * 100);
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-slate-700 capitalize">
                            {item.orderStatus || 'Unknown'}
                          </span>
                          <span className="font-bold text-slate-900">
                            {item.count} orders ({percent}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              item.orderStatus?.toLowerCase() === 'delivered'
                                ? 'bg-emerald-500'
                                : item.orderStatus?.toLowerCase() === 'pending'
                                ? 'bg-amber-500'
                                : item.orderStatus?.toLowerCase() === 'cancelled'
                                ? 'bg-rose-500'
                                : 'bg-indigo-500'
                            }`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payment Status Breakdown */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-semibold text-slate-900 pb-2 border-b border-slate-100">
                Payment Collection Breakdown
              </h3>
              {loading ? (
                <div className="space-y-3 py-4">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="h-4 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3.5">
                  {dashboardData?.breakdown?.paymentStatus?.map((item, idx) => {
                    const total = dashboardData.stats.totalOrders || 1;
                    const percent = Math.round(((item.count || 0) / total) * 100);
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-slate-700 capitalize">
                            {item.paymentStatus || 'Unknown'}
                          </span>
                          <span className="font-bold text-slate-900">
                            {item.count} payments ({percent}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              item.paymentStatus?.toLowerCase() === 'successful'
                                ? 'bg-emerald-500'
                                : item.paymentStatus?.toLowerCase() === 'failed'
                                ? 'bg-rose-500'
                                : 'bg-amber-500'
                            }`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recent Orders and Users Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <RiShoppingCart2Line className="text-indigo-600" /> Recent Orders
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/orders/list')}
                  className="text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                >
                  View All Orders
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Order ID</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Amount</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <TableSkeleton columns={4} rows={4} />
                    ) : dashboardData?.recentOrders?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-xs text-slate-400">
                          No recent orders found.
                        </td>
                      </tr>
                    ) : (
                      dashboardData?.recentOrders?.map((order) => (
                        <tr
                          key={order.orderID}
                          onClick={() => navigate(`/orders/details/${order.orderID}`)}
                          className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-3.5 text-xs font-mono font-bold text-slate-900">
                            #{order.orderID}
                          </td>
                          <td className="px-6 py-3.5 text-xs text-slate-700 font-medium">
                            {order.username || 'N/A'}
                          </td>
                          <td className="px-6 py-3.5 text-xs font-bold text-slate-900">
                            {formatPrice(order.totalAmount)}
                          </td>
                          <td className="px-6 py-3.5 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border capitalize ${getStatusBadge(order.orderStatus)}`}>
                              {order.orderStatus || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Registered Users */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <RiUserLine className="text-blue-600" /> Recent Registered Users
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/users/list')}
                  className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  View All Users
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Email</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase">Verified</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase">Joined Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <TableSkeleton columns={4} rows={4} />
                    ) : dashboardData?.recentUsers?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-xs text-slate-400">
                          No recent users found.
                        </td>
                      </tr>
                    ) : (
                      dashboardData?.recentUsers?.map((user) => (
                        <tr key={user.uid} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-6 py-3.5 text-xs font-bold text-slate-900">
                            {user.username || 'N/A'}
                          </td>
                          <td className="px-6 py-3.5 text-xs text-slate-600 max-w-[160px] truncate">
                            {user.emailID || '-'}
                          </td>
                          <td className="px-6 py-3.5 text-center">
                            {user.verifiedEmail ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                <RiShieldCheckLine className="w-3.5 h-3.5" /> Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                                <RiCloseCircleLine className="w-3.5 h-3.5" /> Unverified
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-3.5 text-center text-xs text-slate-500">
                            {formatDate(user.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </Layout>
  );
};

export default Dashboard;
